import React, { useEffect, useRef, useState } from 'react';
import './player-data-view.styles.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { getUniqueDocumentFromFirestore } from '@/utils/getDataFromDbSimplified';
import UserIcon from '@/components/layout-auth/user-icon/user-icon.component';
import { ModalAlert } from '@/components/modal/modal.component';
import SingleRowData from './single-row-data.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { format } from 'date-fns';
import sizeof from 'firestore-size';

const PlayerDataView = ({
  gameData,
  gameElements,
  gamePlayers
}) => {

  const [ playerData, setPlayerData ] = useState(null);
  const [ alertMessage, setAlertMessage ] = useState(null);
  const { playerId } = useParams();
  const approveRef = useRef();
  const navigate = useNavigate();
  let playerListener;

  useEffect(() => {
    return () => {
      if (playerListener) playerListener();
    }
  }, []);

  useEffect(() => {
    if (!gameData || !gamePlayers || !playerId) return;
    const player = gamePlayers[playerId];
    if (!player) {
      setAlertMessage('Player not found.');
      approveRef.current = () => {
        navigate(-1);
      }
    } else {
      getPlayerData();
    }
  }, [gameData, gamePlayers, playerId])

  function getPlayerData() {
    getUniqueDocumentFromFirestore({
      'path': `games/${gameData.gameId}/players/${playerId}`,
      'callback': callback,
      'handleListener': handleListener
    })
    function callback(data) {
      console.log('Player data', data);
      setPlayerData(data);
    }
    function handleListener(unsub) {
      playerListener = unsub;
    }
  }

  function formatTimestamp(ts) {
    if (!ts) return String(ts);
    try {
      const date = new Date(ts);
      if (!isNaN(date.getTime())) {
        return format(date, 'Pp');
      }
    } catch (e) { /* fall through */ }
    return String(ts);
  }

  function isTimestampKey(key) {
    const lower = key.toLowerCase();
    return lower === 'ts' || lower.includes('timestamp') || lower.endsWith('at') || lower.endsWith('on') || lower === 'time';
  }

  function isTimestampValue(value) {
    return typeof value === 'number' && value > 1000000000000;
  }

  function renderArrayField(key, arr) {
    return (
      <div key={key} className='info-div'>
        <div className='info-div-single'><b>{key}</b></div>
        <div className='object-key'>
          {arr.map((item, i) => {
            if (item && typeof item === 'object') {
              return (
                <div key={i} className='object-key'>
                  <div className='info-div-single'><b>[{i}]</b></div>
                  {Object.keys(item).sort().map(subK => {
                    const subVal = item[subK];
                    if (Array.isArray(subVal)) {
                      return renderArrayField(subK, subVal);
                    }
                    if (subVal && typeof subVal === 'object') {
                      return (
                        <div key={subK} className='object-key'>
                          <div className='info-div-single'><b>{subK}</b></div>
                          {Object.keys(subVal).sort().map(deepK => (
                            <SingleRowData key={deepK} dataKey={deepK} data={subVal[deepK]} />
                          ))}
                        </div>
                      );
                    }
                    return <SingleRowData key={subK} dataKey={subK} data={subVal} />;
                  })}
                </div>
              );
            }
            return (
              <div key={i} className='object-key-div'>
                <SingleRowData dataKey={String(i)} data={item} />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderElementCollection(sectionKey, obj) {
    const entries = Object.keys(obj).sort();
    return (
      <div key={sectionKey} className='info-section'>
        <div className='info-section-header'><b>{sectionKey}</b></div>
        <div className='element-collection'>
          {entries.map(elementId => {
            const element = gameElements[elementId];
            const entryData = obj[elementId];
            if (!element) return null;
            return (
              <div key={elementId} className='element-entry'>
                <div className='element-entry-icon'>
                  <ImageContainer src={element.icon} />
                </div>
                <div className='element-entry-info'>
                  <div className='element-entry-name'>{element.name}</div>
                  {
                    Array.isArray(entryData)
                    ?
                    <div className='element-entry-meta'>
                      <span className='meta-key'>values:</span>
                      <span className='meta-value'>{entryData.map(String).join(', ')}</span>
                    </div>
                    :
                    typeof entryData === 'object' && entryData !== null
                    ?
                    Object.keys(entryData).sort().map(entryKey => {
                      const val = entryData[entryKey];
                      const display = (isTimestampKey(entryKey) || isTimestampValue(val))
                        ? formatTimestamp(val)
                        : typeof val === 'boolean' ? (val ? 'True' : 'False') : String(val);
                      return (
                        <div key={entryKey} className='element-entry-meta'>
                          <span className='meta-key'>{entryKey}:</span>
                          <span className='meta-value'>{display}</span>
                        </div>
                      );
                    })
                    :
                    <div className='element-entry-meta'>
                      <span className='meta-value'>
                        {isTimestampValue(entryData)
                          ? formatTimestamp(entryData)
                          : String(entryData)}
                      </span>
                    </div>
                  }
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderObjectField(key, data) {
    const subKeys = Object.keys(data);
    const elementMatchCount = subKeys.filter(k => gameElements && gameElements[k]).length;

    if (elementMatchCount > 0) {
      return renderElementCollection(key, data);
    }

    return (
      <div key={key} className='info-div'>
        <div className='info-div-single'><b>{key}</b></div>
        <div className='object-key'>
          {subKeys.sort().map(k => {
            const v = data[k];
            if (Array.isArray(v)) {
              return renderArrayField(k, v);
            }
            if (v && typeof v === 'object') {
              return (
                <div key={k} className='object-key'>
                  <div className='info-div-single'><b>{k}</b></div>
                  {Object.keys(v).sort().map(subK => (
                    <SingleRowData key={subK} dataKey={subK} data={v[subK]} />
                  ))}
                </div>
              );
            }
            return (
              <div key={k} className='object-key-div'>
                <SingleRowData dataKey={k} data={v} />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className='player-data-view'>
      {
        (playerData) &&
        <div className='player-data-content'>
          <div className='player-data-header'>
            <div className='player-icon'>
              <UserIcon profile={playerData} noHover={true} />
            </div>
            <div className='name'>
              <h2>{playerData.fName} {playerData.lName ?? ''}</h2>
              <div>{playerData.team ? 'Team' : 'Player'}</div>
              <div className='info-box__id meta'>
                Size: {((sizeof(playerData) / 975000) * 100).toLocaleString()}% ({(sizeof(playerData) / 1000).toLocaleString()} kb)
              </div>
            </div>
          </div>

          <div className='g-space-1'></div>

          {
            Object.keys(playerData)
              .sort((a, b) => {
                if (a === 'newStuff') return 1;
                if (b === 'newStuff') return -1;
                const aIsComplex = Array.isArray(playerData[a]) || (playerData[a] && typeof playerData[a] === 'object');
                const bIsComplex = Array.isArray(playerData[b]) || (playerData[b] && typeof playerData[b] === 'object');
                if (aIsComplex !== bIsComplex) return aIsComplex ? 1 : -1;
                return a.localeCompare(b);
              })
              .map(key => {
                const data = playerData[key];
                if (Array.isArray(data)) {
                  return renderArrayField(key, data);
                }
                if (data && typeof data === 'object') {
                  return renderObjectField(key, data);
                }
                return (
                  <div key={key} className='info-div-single'>
                    <SingleRowData dataKey={key} data={data} />
                  </div>
                );
              })
          }
        </div>
      }
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        onApprove={approveRef.current} />
    </div>
  )
}

export default PlayerDataView
